import React from 'react'
import styled from 'styled-components'
import { APP_STORE_URLS } from 'utility/whatsapp-utils'
import { DesktopMode, MobileMode } from 'layout'

const DownloadContainer = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
    margin: 20px 0;
`

const CompactContainer = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
    margin: 0;
`

const DesktopDownloadButton = styled.a`
    display: flex;
    align-items: center;
    background: #000;
    color: white;
    text-decoration: none;
    border-radius: 6px;
    padding: 6px 10px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    min-width: 140px;
    height: 40px;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        text-decoration: none;
        color: white;
    }

    &:active {
        transform: translateY(0);
    }
`

/** Icon-only store badges for crowded headers (mobile / Free chip). */
const CompactDownloadButton = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #000;
    color: white;
    text-decoration: none;
    border-radius: 6px;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        text-decoration: none;
        color: white;
    }

    &:active {
        transform: translateY(0);
    }
`

const ButtonContent = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
`

const DesktopButtonText = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.1;
`

const DesktopSmallText = styled.span`
    font-size: 9px;
    opacity: 0.9;
    font-weight: 400;
`

const DesktopLargeText = styled.span`
    font-size: 12px;
    font-weight: 500;
`

const DesktopIconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
`

const CompactIconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
`

const GooglePlayIcon = () => (
    <svg width="100%" height="100%" viewBox="0 0 24 24">
        <path
            d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5Z"
            fill="#00D4AA"
        />
        <path
            d="M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12Z"
            fill="#00A0F3"
        />
        <path
            d="M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81Z"
            fill="#FFD700"
        />
        <path
            d="M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"
            fill="#FF6B35"
        />
    </svg>
)

const AppleIcon = () => (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
    </svg>
)

const CompactButtons = ({ googlePlayUrl, appStoreUrl }) => (
    <CompactContainer>
        <CompactDownloadButton
            href={googlePlayUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Get it on Google Play"
            aria-label="Download eAuditor on Google Play"
        >
            <CompactIconContainer>
                <GooglePlayIcon />
            </CompactIconContainer>
        </CompactDownloadButton>
        <CompactDownloadButton
            href={appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Download on the App Store"
            aria-label="Download eAuditor on App Store"
        >
            <CompactIconContainer>
                <AppleIcon />
            </CompactIconContainer>
        </CompactDownloadButton>
    </CompactContainer>
)

const FullButtons = ({ googlePlayUrl, appStoreUrl }) => (
    <DownloadContainer>
        <DesktopDownloadButton
            href={googlePlayUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download eAuditor on Google Play"
        >
            <ButtonContent>
                <DesktopIconContainer>
                    <GooglePlayIcon />
                </DesktopIconContainer>
                <DesktopButtonText>
                    <DesktopSmallText>GET IT ON</DesktopSmallText>
                    <DesktopLargeText>Google Play</DesktopLargeText>
                </DesktopButtonText>
            </ButtonContent>
        </DesktopDownloadButton>
        <DesktopDownloadButton
            href={appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download eAuditor on App Store"
        >
            <ButtonContent>
                <DesktopIconContainer>
                    <AppleIcon />
                </DesktopIconContainer>
                <DesktopButtonText>
                    <DesktopSmallText>Download on the</DesktopSmallText>
                    <DesktopLargeText>App Store</DesktopLargeText>
                </DesktopButtonText>
            </ButtonContent>
        </DesktopDownloadButton>
    </DownloadContainer>
)

/**
 * @param {'auto'|'full'|'compact'} [variant='auto']
 *   auto  = desktop full badges, mobile compact icons (header-safe)
 *   full  = always text badges
 *   compact = always icon-only
 */
export const AppDownloadButtons = ({ variant = 'auto' } = {}) => {
    const googlePlayUrl = APP_STORE_URLS.GOOGLE_PLAY
    const appStoreUrl = APP_STORE_URLS.APP_STORE

    if (variant === 'compact') {
        return (
            <CompactButtons
                googlePlayUrl={googlePlayUrl}
                appStoreUrl={appStoreUrl}
            />
        )
    }

    if (variant === 'full') {
        return (
            <FullButtons
                googlePlayUrl={googlePlayUrl}
                appStoreUrl={appStoreUrl}
            />
        )
    }

    return (
        <>
            <DesktopMode>
                <FullButtons
                    googlePlayUrl={googlePlayUrl}
                    appStoreUrl={appStoreUrl}
                />
            </DesktopMode>
            <MobileMode>
                <CompactButtons
                    googlePlayUrl={googlePlayUrl}
                    appStoreUrl={appStoreUrl}
                />
            </MobileMode>
        </>
    )
}
